import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

type Cita = {
  id: string;
  fecha_inicio: string;
  estado: 'solicitada' | 'confirmada' | 'completada' | 'cancelada' | 'ausente';
  notas_socio: string | null;
  usuario: { nombre: string; email: string } | null;
};

type UsuarioOption = { id: string; nombre: string; email: string; gimnasio_id: string; sucursal_id: string };

export default function EvaluacionesAgendaModal({ sucursalId, onClose }: { sucursalId: string; onClose: () => void }) {
  const [citas, setCitas] = useState<Cita[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usuarios, setUsuarios] = useState<UsuarioOption[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [usuarioId, setUsuarioId] = useState('');
  const [fecha, setFecha] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [hora, setHora] = useState('10:00');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: queryError } = await supabase
      .from('progreso_evaluacion_citas')
      .select('id,fecha_inicio,estado,notas_socio,usuario:usuarios(nombre,email)')
      .eq('sucursal_id', sucursalId)
      .gte('fecha_inicio', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
      .order('fecha_inicio');
    setLoading(false);
    if (queryError) return setError(queryError.message);
    setCitas((data || []) as unknown as Cita[]);
  }, [sucursalId]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    void supabase.from('usuarios').select('id,nombre,email,gimnasio_id,sucursal_id').eq('sucursal_id', sucursalId).eq('estado', 'activo').order('nombre')
      .then(({ data }) => setUsuarios((data || []) as UsuarioOption[]));
  }, [sucursalId]);

  const changeStatus = async (id: string, estado: Cita['estado']) => {
    const { error: updateError } = await supabase.from('progreso_evaluacion_citas').update({ estado }).eq('id', id);
    if (updateError) return setError(updateError.message);
    await load();
  };

  const createAppointment = async () => {
    const usuario = usuarios.find((item) => item.id === usuarioId);
    if (!usuario || !fecha || !hora) return setError('Selecciona socio, fecha y hora.');
    setSaving(true); setError('');
    const fechaInicio = new Date(`${fecha}T${hora}:00`);
    const { error: insertError } = await supabase.from('progreso_evaluacion_citas').insert({
      gimnasio_id: usuario.gimnasio_id,
      sucursal_id: usuario.sucursal_id,
      usuario_id: usuario.id,
      fecha_inicio: fechaInicio.toISOString(),
      estado: 'confirmada',
    });
    setSaving(false);
    if (insertError) {
      if (insertError.code === '23505') return setError('Este socio ya tiene una evaluación activa.');
      if (insertError.code === '23P01') return setError('Ese horario se cruza con otra evaluación. Selecciona una hora disponible.');
      return setError(insertError.message);
    }
    setShowCreate(false); setUsuarioId(''); await load();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden shadow-2xl">
        <header className="flex items-center justify-between p-6 border-b">
          <div><h2 className="text-2xl font-bold">Evaluaciones de avance</h2><p className="text-gray-500">Solicitudes de medición de los socios</p></div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowCreate((value) => !value)} className="bg-green-800 text-white rounded-lg px-4 py-2 font-semibold">+ Agendar cita</button>
            <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-gray-100 text-2xl" aria-label="Cerrar">×</button>
          </div>
        </header>
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-105px)]">
          {error && <div className="mb-4 rounded-lg bg-red-50 text-red-700 p-3">{error}</div>}
          {showCreate && <div className="mb-5 border rounded-xl bg-gray-50 p-4">
            <h3 className="font-bold mb-3">Nueva evaluación confirmada</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <select value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)} className="border rounded-lg px-3 py-2 bg-white"><option value="">Seleccionar socio</option>{usuarios.map((u) => <option key={u.id} value={u.id}>{u.nombre} · {u.email}</option>)}</select>
              <input type="date" min={new Date().toISOString().slice(0, 10)} value={fecha} onChange={(e) => setFecha(e.target.value)} className="border rounded-lg px-3 py-2" />
              <input type="time" value={hora} onChange={(e) => setHora(e.target.value)} className="border rounded-lg px-3 py-2" />
            </div>
            <div className="flex justify-end gap-2 mt-3"><button onClick={() => setShowCreate(false)} className="px-4 py-2">Cancelar</button><button disabled={saving} onClick={() => void createAppointment()} className="bg-green-800 text-white rounded-lg px-4 py-2 disabled:opacity-50">{saving ? 'Guardando…' : 'Confirmar cita'}</button></div>
          </div>}
          {loading ? <p className="text-gray-500">Cargando agenda…</p> : citas.length === 0 ? (
            <div className="py-16 text-center text-gray-500"><i className="ri-calendar-check-line text-5xl"/><p className="mt-3">No hay evaluaciones próximas.</p></div>
          ) : <div className="space-y-3">{citas.map((cita) => (
            <div key={cita.id} className="border rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1">
                <div className="font-bold text-gray-900">{cita.usuario?.nombre || 'Socio'}</div>
                <div className="text-sm text-gray-500">{new Date(cita.fecha_inicio).toLocaleString('es-CL', { dateStyle: 'full', timeStyle: 'short' })}</div>
                {cita.notas_socio && <div className="text-sm mt-2 text-gray-700">“{cita.notas_socio}”</div>}
              </div>
              <span className="px-3 py-1 rounded-full bg-gray-100 text-sm capitalize">{cita.estado}</span>
              <select value={cita.estado} onChange={(e) => void changeStatus(cita.id, e.target.value as Cita['estado'])} className="border rounded-lg px-3 py-2 bg-white">
                <option value="solicitada">Solicitada</option><option value="confirmada">Confirmada</option><option value="completada">Completada</option><option value="cancelada">Cancelada</option><option value="ausente">No asistió</option>
              </select>
            </div>
          ))}</div>}
        </div>
      </div>
    </div>
  );
}
